import PropTypes from 'prop-types'
import { Handle, Position, useUpdateNodeInternals } from '@xyflow/react'
import React,{ useEffect, useRef, useState, useContext } from 'react'
import { useSelector } from 'react-redux'

import { IconArrowsMaximize, IconEdit, IconAlertTriangle, IconBraces} from '@tabler/icons-react'

// project import
import { Dropdown } from '@/components/dropdown/Dropdown'
import { AsyncDropdown } from '@/components/dropdown/AsyncDropdown'
import { Input } from '@/components/input/Input'
import { File } from '@/components/file/File'
import { SwitchInput } from '@/components/switch/Switch'
import { FlowContext } from '@/store/context/ReactFlowContext'
import { isValidConnection } from '@/utils/genericHelper'
import { JsonEditorInput } from '@/components/json/JsonEditor'
import { TooltipWithParser } from '@/components/tooltip/TooltipWithParser'
// import ToolDialog from 'views/tools/ToolDialog'
import FormatPromptValuesDialog from '@/components/dialog/FormatPromptValuesDialog'
import CredentialInputHandler from './CredentialInputHandler'
import { Tooltip as ReactTooltip } from 'react-tooltip';
// utils
import { getInputVariables } from '@/utils/genericHelper'
import DoubleBracket from '@/components/tiptap/DoubleBracket'
// const
import { FLOWISE_CREDENTIAL_ID } from '@/store/constant'

import useApi from '@/hooks/useApi'
import nodesApi from '@/api/nodes'
import { Button } from '@/components/ui/button'
import { RootState } from '@/store'

const EDITABLE_TOOLS = ['selectedTool']


interface CustomWidthTooltipProps {
    title: string; // or any other specific type you want
    children: React.ReactNode; // Add children prop
}

const CustomWidthTooltip: React.FC<CustomWidthTooltipProps> = ({ title, children }) => (
    <ReactTooltip
        className="max-w-xs" // Tailwind class for max-width
        place="top"
        data-tip={title} // Using data-tip to associate the tooltip with children
    >
        {children} {/* Render the children here */}
    </ReactTooltip>
);

// ===========================|| NodeInputHandler ||=========================== //

const NodeInputHandler = ({ inputAnchor, inputParam, data, disabled = false, isAdditionalParams = false }) => {

    let i = 0
    let x = 0
    const customization = useSelector((state: RootState) => state.customization)
    const ref = useRef<HTMLDivElement | null>(null);

    const { reactFlowInstance } = useContext(FlowContext)
    const updateNodeInternals = useUpdateNodeInternals()
    const [position, setPosition] = useState(0)
    const [showExpandDialog, setShowExpandDialog] = useState(false)
    const [expandDialogProps, setExpandDialogProps] = useState({})
    const [showAsyncOptionDialog, setAsyncOptionEditDialog] = useState('')
    const [asyncOptionEditDialogProps, setAsyncOptionEditDialogProps] = useState({})
    const [reloadTimestamp, setReloadTimestamp] = useState(Date.now().toString())
    const [showFormatPromptValuesDialog, setShowFormatPromptValuesDialog] = useState(false)
    const [formatPromptValuesDialogProps, setFormatPromptValuesDialogProps] = useState({})
    const [inputShowHide, setInputShowHide] = useState(false)

    const getOptionsLoadOptionsRefresh = useApi(nodesApi.getOptionsWhenLoadOptionsRefreshsOn)

    const onExpandDialogClicked = (value, inputParam) => {
        const dialogProp = {
            value,
            inputParam,
            disabled,
            confirmButtonName: 'Save',
            cancelButtonName: 'Cancel'
        }
        setExpandDialogProps(dialogProp)
        setShowExpandDialog(true)
    }

    const onFormatPromptValuesClicked = (value, inputParam) => {
        // Preset values if the field is format prompt values
        let inputValue = value
        if (inputParam.name === 'promptValues' && !value) {
            const obj: any = {}
            const templateValue =
                (data.inputs['template'] ?? '') + (data.inputs['systemMessagePrompt'] ?? '') + (data.inputs['humanMessagePrompt'] ?? '')
            const inputVariables = getInputVariables(templateValue)
            for (const inputVariable of inputVariables) {
                obj[inputVariable] = ''
            }
            if (Object.keys(obj).length) inputValue = JSON.stringify(obj)
        }

        if (reactFlowInstance) {
        const dialogProp = {
            value: inputValue,
            inputParam,
            nodes: reactFlowInstance.getNodes(),
            edges: reactFlowInstance.getEdges(),
            nodeId: data.id
        }
    

        setFormatPromptValuesDialogProps(dialogProp)
        setShowFormatPromptValuesDialog(true)
    }
    }

    const onExpandDialogSave = (newValue, inputParamName) => {
        setShowExpandDialog(false)
        data.inputs[inputParamName] = newValue
    }

    const editAsyncOption = (inputParamName, inputValue) => {
        if (inputParamName === 'selectedTool') {
            setAsyncOptionEditDialogProps({
                title: 'Edit Tool',
                type: 'EDIT',
                cancelButtonName: 'Cancel',
                confirmButtonName: 'Save',
                toolId: inputValue
            })
        }
        setAsyncOptionEditDialog(inputParamName)
    }

    const addAsyncOption = (inputParamName) => {
        if (inputParamName === 'selectedTool') {
            setAsyncOptionEditDialogProps({
                title: 'Add New Tool',
                type: 'ADD',
                cancelButtonName: 'Cancel',
                confirmButtonName: 'Add'
            })
        }
        setAsyncOptionEditDialog(inputParamName)
    }

    const onConfirmAsyncOption = (selectedOptionId = '') => {
        if (!selectedOptionId) {
            data.inputs[showAsyncOptionDialog] = ''
        } else {
            data.inputs[showAsyncOptionDialog] = selectedOptionId
            setReloadTimestamp(Date.now().toString())
        }
        setAsyncOptionEditDialogProps({})
        setAsyncOptionEditDialog('')
    }

    const func = (child) => {
        return Object?.keys(inputParam?.displayOptions?.show || {}).map((key, ind) => {
            if (inputParam?.displayOptions?.show[key][0] === data.inputs[key]) {
                i += 1
                if (
                    i === Object?.keys(inputParam?.displayOptions?.show || {}).length &&
                    Object?.keys(inputParam?.displayOptions?.show || {}).length - 1 === ind
                ) {
                    return child
                }
            }
        })
    }

    useEffect(() => {
        if (ref.current && ref.current.offsetTop !== undefined && ref.current.clientHeight !== undefined) {
            setPosition(ref.current.offsetTop + ref.current.clientHeight / 2);
            updateNodeInternals(data.id);
        }
    }, [data.id, ref, updateNodeInternals]);


    useEffect(() => {
        updateNodeInternals(data.id)
    }, [data.id, position, updateNodeInternals])

    useEffect(() => {
        if (inputParam?.typeOptions !== undefined) {
            const body = {}
            if (inputParam?.typeOptions?.loadOptionsRefreshsOn !== undefined) {
                inputParam?.typeOptions?.loadOptionsRefreshsOn.forEach((element) => {
                    body[`${element}`] = data?.inputs[element]
                })
                getOptionsLoadOptionsRefresh.request(data?.name, inputParam?.typeOptions?.loadOptionsMethod, body)
                inputParam.options = getOptionsLoadOptionsRefresh?.data
            } else {
                getOptionsLoadOptionsRefresh.request(data?.name, inputParam?.typeOptions?.loadOptionsMethod, body)
                inputParam.options = getOptionsLoadOptionsRefresh?.data
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data])

    useEffect(() => {
        data.inputs[inputParam?.name] = inputParam?.default
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])


    const [inputVisibility, setInputVisibility] = useState({}); // Manages visibility for each input field
    const [additionalTextInputs, setAdditionalTextInputs] = useState({}); // Manages values for new text inputs

    const handleToggle = (inputName) => {
        setInputVisibility(prevState => ({
            ...prevState,
            [inputName]: !prevState[inputName]
        }));
        
        if (!inputVisibility[inputName]) {
            setAdditionalTextInputs(prevState => ({
                ...prevState,
                [inputName]: '' // Reset text input when toggling back
            }));
        }
    };

    const handleTextInputChange = (e, inputName) => {
        setAdditionalTextInputs(prevState => ({
            ...prevState,
            [inputName]: e.target.value
        }));
    };

    const [isChecked, setIsChecked] = useState(false);

    const handleModel = () => setIsChecked(!isChecked)


    return (
        <div ref={ref} style={{ clear: 'left' }} onClick={handleModel}>
                    {inputAnchor && (
                        <>
                            <Handle
                                type='target'
                                position={Position.Left}
                                key={inputAnchor.id}
                                id={inputAnchor.id}
                                isValidConnection={(connection) => isValidConnection(connection, reactFlowInstance)}
                                style={{
                                    height: 10,
                                    width: 10,
                                    // backgroundColor: data.selected ? theme.palette.primary.main : theme.palette.text.secondary,
                                    top: position
                                }}
                            /> 
                            <div className="p-2">
                                <p className="text-base" >
                                    {inputAnchor.label}
                                    {!inputAnchor.optional && <span className="text-red-500">*</span>}
                                    {inputAnchor.description && (
                                        <TooltipWithParser  title={inputAnchor.description} />
                                    )}
                                </p>
                            </div>
                        </>
                    )}

            {((inputParam && !inputParam.additionalParams) || isAdditionalParams) && (
                <>
                    {inputParam.acceptVariable && (
                        <CustomWidthTooltip title={inputParam.type}>
                            <Handle
                                type='target'
                                position={Position.Left}
                                key={inputParam.id}
                                id={inputParam.id}
                                isValidConnection={(connection) => isValidConnection(connection, reactFlowInstance)}
                                style={{
                                    height: 10,
                                    width: 10,
                                    // backgroundColor: data.selected ? theme.palette.primary.main : theme.palette.text.secondary,
                                    top: position
                                }}
                            />
                        </CustomWidthTooltip>
                    )}
                   <div className="py-1 px-2">
                        <div className='flex flex-row items-center '>
                            {!('displayOptions' in inputParam) ? (
                                <>
                                    <p className="text-xs font-sans font-normal " >
                                        {inputParam.label}
                                        {!inputParam.optional && <span className="text-red-500">&nbsp;*</span>}
                                        {inputParam.description && (
                                            <TooltipWithParser  title={inputParam.description} />
                                        )}
                                    </p>
                                </>
                            ) : (
                                Object?.keys(inputParam?.displayOptions?.show || {}).map((key, ind) => {
                                    
                                    if (inputParam?.displayOptions?.show[key][0] === data.inputs[key]) {
                                        x += 1
                                        if (
                                            x === Object?.keys(inputParam?.displayOptions?.show || {}).length &&
                                            Object?.keys(inputParam?.displayOptions?.show || {}).length - 1 === ind
                                        ) {
                                            return (
                                                <p key={ind} className="text-base" >
                                                {inputParam.label}
                                                {!inputParam.optional && <span className="text-red-500">&nbsp;*</span>}
                                                {inputParam.description && (
                                                    <TooltipWithParser  title={inputParam.description} />
                                                )}
                                            </p>
                                            )
                                        }
                                    }
                                })
                            )}
                            <div style={{ flexGrow: 1 }}></div>
                            {inputParam.type === 'string' && inputParam.rows && (
                                <button
                                className="h-6 w-6 text-blue-500 hover:text-blue-700 focus:outline-none" // Adjust color and sizes as needed
                                title="Expand"
                                onClick={() =>
                                    onExpandDialogClicked(data.inputs[inputParam.name] ?? inputParam.default ?? '', inputParam)
                                }
                            >
                                <IconArrowsMaximize className="w-4 h-4" /> {/* Adjust size of the icon */}
                            </button>
                            
                            )}
                      
                        {inputParam.warning && (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    borderRadius: 10,
                                    background: 'rgb(254,252,191)',
                                    padding: 10,
                                    marginTop: 10,
                                    marginBottom: 10
                                }}
                            >
                                <IconAlertTriangle size={36} color='orange' />
                                <span style={{ color: 'rgb(116,66,16)', marginLeft: 10 }}>{inputParam.warning}</span>
                            </div>
                        )}
                        {inputParam.type === 'credential' && !('displayOptions' in inputParam) ? (
                            <CredentialInputHandler
                                disabled={disabled}
                                data={data}
                                inputParam={inputParam}
                                onSelect={(newValue) => {
                                    data.credential = newValue
                                    data.inputs[FLOWISE_CREDENTIAL_ID] = newValue // in case data.credential is not updated
                                }}
                            />
                        ) : (
                            ''
                        )}
                        {inputParam.type === 'file' && !('displayOptions' in inputParam) ? (
                            <File
                                disabled={disabled}
                                fileType={inputParam.fileType || '*'}
                                onChange={(newValue) => (data.inputs[inputParam.name] = newValue)}
                                value={data.inputs[inputParam.name] ?? inputParam.default ?? 'Choose a file to upload'}
                            />
                        ) : (
                            ''
                        )}
                        {inputParam.type === 'boolean' && !('displayOptions' in inputParam) && (
                            <SwitchInput
                                disabled={disabled}
                                onChange={(newValue) => (data.inputs[inputParam.name] = newValue)}
                                value={data.inputs[inputParam.name] ?? inputParam.default ?? false}
                            />
                        )}
                        {/* {(inputParam.type === 'string' || inputParam.type === 'password' || inputParam.type === 'number') &&
                            !('displayOptions' in inputParam) && (
                                <div className='flex flex-col'>
                                    <div className="flex justify-end">
                                            <button className='flex items-center p-0'>
                                            <IconBraces size={14} stroke={1}/>
                                            </button>
                                    </div>
                                <Input
                                    key={data.inputs[inputParam.name]}
                                    disabled={disabled}
                                    inputParam={inputParam}
                                    onChange={(newValue) => (data.inputs[inputParam.name] = newValue)}
                                    value={data.inputs[inputParam.name] ?? inputParam.default ?? ''}
                                    showDialog={showExpandDialog}
                                    dialogProps={expandDialogProps}
                                    onDialogCancel={() => setShowExpandDialog(false)}
                                    onDialogConfirm={(newValue, inputParamName) => onExpandDialogSave(newValue, inputParamName)}
                                />
                                </div>
                               
                            )} */}

                    {(inputParam.type === 'string' || inputParam.type === 'password' || inputParam.type === 'number') &&
                        !('displayOptions' in inputParam) && (
                            <div key={inputParam.name} className='mb-4'>
                                <div className="flex justify-end">
                                    <button
                                        className='flex items-center p-0'
                                        onClick={() => handleToggle(inputParam.name)}
                                    >
                                        <IconBraces size={14} stroke={1} />
                                    </button>
                                </div>
                                {inputVisibility[inputParam.name] ? (
                                    
                                    <DoubleBracket 
                                        key={data.inputs[inputParam.name]}
                                        inputParam={inputParam}
                                        onChange={(newValue) => {
                                            console.log(`Value: ${newValue}, Name: ${inputParam?.name}`);
                                            data.inputs[inputParam.name] = newValue;
                                        }}
                                        content={data.inputs[inputParam.name] ?? inputParam.default ?? ''}
                                        isChecked={isChecked}
                                    />
                                ) : (
                                    <Input
                                        key={data.inputs[inputParam.name]}
                                        disabled={disabled}
                                        inputParam={inputParam}
                                        onChange={(newValue) => {
                                            console.log(`Value: ${newValue}, Name: ${inputParam.name}`);
                                            data.inputs[inputParam.name] = newValue;
                                        }}
                                        value={data.inputs[inputParam.name] ?? inputParam.default ?? ''}
                                        showDialog={showExpandDialog}
                                        dialogProps={expandDialogProps}
                                        onDialogCancel={() => setShowExpandDialog(false)}
                                        onDialogConfirm={(newValue, inputParamName) => onExpandDialogSave(newValue, inputParamName)}
                                    />
                                )}
                            </div>
                        )}

                        {inputParam.type === 'json' && !('displayOptions' in inputParam) && (
                            <>
                                {!inputParam?.acceptVariable && (
                                    <JsonEditorInput
                                        disabled={disabled}
                                        onChange={(newValue) => (data.inputs[inputParam.name] = newValue)}
                                        value={data.inputs[inputParam.name] ?? inputParam.default ?? ''}
                                        isDarkMode={customization.isDarkMode}
                                    />
                                )}
                                {inputParam?.acceptVariable && (
                                    <>
                                        <button
                                            className="w-full rounded-full border border-gray-300 p-2 mb-2 mt-2 text-gray-700 hover:bg-gray-100 focus:outline-none"
                                            onClick={() => onFormatPromptValuesClicked(data.inputs[inputParam.name] ?? '', inputParam)}
                                        >
                                            Format Prompt Values
                                        </button>

                                        <FormatPromptValuesDialog
                                            show={showFormatPromptValuesDialog}
                                            dialogProps={formatPromptValuesDialogProps}
                                            onCancel={() => setShowFormatPromptValuesDialog(false)}
                                            onChange={(newValue) => (data.inputs[inputParam.name] = newValue)}
                                        ></FormatPromptValuesDialog>
                                    </>
                                )}
                            </>
                        )}
                        {inputParam.type === 'options' && !('displayOptions' in inputParam) ? (
                            <Dropdown
                                disabled={disabled}
                                name={inputParam.name}
                                options={inputParam.options}
                                onSelect={(newValue) => (data.inputs[inputParam.name] = newValue)}
                                value={data.inputs[inputParam.name] ?? inputParam.default ?? 'choose an option'}
                            />
                        ) : (
                            func(
                                <Dropdown
                                    disabled={disabled}
                                    name={inputParam.name}
                                    options={inputParam.options}
                                    onSelect={(newValue) => (data.inputs[inputParam.name] = newValue)}
                                    value={data.inputs[inputParam.name] ?? inputParam.default ?? 'choose an option'}
                                />
                            )
                        )}
                        {inputParam.type === 'asyncOptions' && (
                            <>
                                {data.inputParams.length === 1 && <div style={{ marginTop: 10 }} />}
                                <div key={reloadTimestamp} style={{ display: 'flex', flexDirection: 'row' }}>
                                    <AsyncDropdown
                                        disabled={disabled}
                                        name={inputParam.name}
                                        nodeData={data}
                                        value={data.inputs[inputParam.name] ?? inputParam.default ?? 'choose an option'}
                                        isCreateNewOption={EDITABLE_TOOLS.includes(inputParam.name)}
                                        onSelect={(newValue) => (data.inputs[inputParam.name] = newValue)}
                                        onCreateNew={() => addAsyncOption(inputParam.name)}
                                    />
                                    {EDITABLE_TOOLS.includes(inputParam.name) && data.inputs[inputParam.name] && (
                                        <button
                                        className="h-6 w-6 text-blue-500 hover:text-blue-700 focus:outline-none" // Adjust color and size as needed
                                        title="Edit"
                                        onClick={() => editAsyncOption(inputParam.name, data.inputs[inputParam.name])}
                                    >
                                        <IconEdit className="w-4 h-4" /> {/* Adjust icon size as needed */}
                                    </button>
                                    
                                    )}
                                </div>
                                
                            </>
                        )}
                          </div>
                    </div>
                </>
            )}
            {/* <ToolDialog
                show={EDITABLE_TOOLS.includes(showAsyncOptionDialog)}
                dialogProps={asyncOptionEditDialogProps}
                onCancel={() => setAsyncOptionEditDialog('')}
                onConfirm={onConfirmAsyncOption}
            ></ToolDialog> */}
        </div>
    )
}

NodeInputHandler.propTypes = {
    inputAnchor: PropTypes.object,
    inputParam: PropTypes.object,
    data: PropTypes.object,
    disabled: PropTypes.bool,
    isAdditionalParams: PropTypes.bool
}

export default NodeInputHandler
